<template>
    <AmModal
        :open="open"
        size="wide"
        eyebrow="ПОДДЕРЖКА"
        title="Обращения"
        subtitle="Диалог с командой поддержки · ответы приходят с уведомлением"
        body-flush
        @close="$emit('close')"
    >
            <div class="tickets-layout" :class="{ 'tickets-layout--chat': mobileChatOnly }">
                <aside v-show="!mobileChatOnly" class="tickets-list-panel">
                    <AmButton variant="primary" block @click="showComposer = true">
                        + Новое обращение
                    </AmButton>

                    <div v-if="showComposer" class="composer bento-card">
                        <div class="composer-fields">
                            <div class="bento-field composer-field">
                                <label class="bento-label">Тема</label>
                                <input v-model="newTicket.subject" type="text" class="bento-input" placeholder="Кратко опишите вопрос" maxlength="200" />
                            </div>
                            <div class="bento-field composer-field">
                                <label class="bento-label">Категория</label>
                                <AmSelect v-model="newTicket.category" :options="categoryOptions" placeholder="Категория" />
                            </div>
                            <div class="bento-field composer-field">
                                <label class="bento-label">Сообщение</label>
                                <textarea v-model="newTicket.message" class="bento-textarea" rows="3" placeholder="Опишите вопрос…" />
                            </div>
                        </div>
                        <div class="composer-actions">
                            <AmButton variant="ghost" size="sm" @click="showComposer = false">Отмена</AmButton>
                            <AmButton variant="primary" size="sm" :disabled="creating" @click="createTicket">
                                {{ creating ? '…' : 'Создать' }}
                            </AmButton>
                        </div>
                    </div>

                    <ChatSkeleton v-if="listLoading" variant="list" :count="4" />
                    <ul v-else class="ticket-items">
                        <li
                            v-for="t in tickets"
                            :key="t.id"
                            class="ticket-item"
                            :class="{ 'ticket-item--active': selectedId === t.id, 'ticket-item--unread': t.unread }"
                            @click="openTicket(t.id)"
                        >
                            <div class="ticket-item-head">
                                <span class="ticket-subject">{{ t.subject }}</span>
                                <span v-if="t.unread" class="unread-dot" aria-label="Новый ответ" />
                            </div>
                            <p class="ticket-preview bento-muted bento-small">{{ t.last_message_preview }}</p>
                            <div class="ticket-item-meta">
                                <span class="bento-pill" :class="ticketStatusPillClass(t.status)">
                                    {{ formatTicketStatus(t.status) }}
                                </span>
                                <span class="ticket-date bento-muted bento-small">{{ formatDate(t.updated_at || t.created_at) }}</span>
                            </div>
                        </li>
                    </ul>
                    <p v-if="!listLoading && tickets.length === 0" class="state bento-muted">Обращений пока нет</p>
                </aside>

                <section class="tickets-chat-panel">
                    <template v-if="selectedTicket">
                        <header class="chat-head bento-card">
                            <button
                                v-if="mobileChatOnly"
                                type="button"
                                class="chat-back"
                                aria-label="К списку обращений"
                                @click="closeChatView"
                            >
                                <ChatIcon name="arrow-left" :size="16" />
                            </button>
                            <div class="chat-head-text">
                                <h3 class="chat-title">{{ selectedTicket.subject }}</h3>
                                <span v-if="formatTicketCategory(selectedTicket.category)" class="bento-muted bento-small">
                                    {{ formatTicketCategory(selectedTicket.category) }}
                                </span>
                            </div>
                            <span class="bento-pill" :class="ticketStatusPillClass(selectedTicket.status)">
                                {{ formatTicketStatus(selectedTicket.status) }}
                            </span>
                        </header>

                        <div ref="messagesEl" class="messages">
                            <ChatSkeleton v-if="chatLoading" variant="messages" :count="4" />
                            <template v-else>
                                <div
                                    v-for="msg in selectedTicket.messages || []"
                                    :key="msg.id"
                                    class="msg"
                                    :class="msg.is_staff ? 'msg--staff' : 'msg--user'"
                                >
                                    <div class="msg-meta bento-small bento-muted">
                                        {{ msg.author_name || (msg.is_staff ? 'Поддержка' : 'Вы') }}
                                        · {{ formatTime(msg.created_at) }}
                                    </div>
                                    <div class="msg-bubble">{{ msg.body }}</div>
                                </div>
                            </template>
                        </div>

                        <form class="reply-form" @submit.prevent="sendMessage">
                            <div class="reply-card" :class="{ 'reply-card--disabled': sending }">
                                <input
                                    v-model="replyText"
                                    type="text"
                                    class="reply-input"
                                    placeholder="Напишите сообщение…"
                                    :disabled="sending"
                                />
                                <button
                                    type="submit"
                                    class="send-btn"
                                    :disabled="sending || !replyText.trim()"
                                    aria-label="Отправить"
                                >
                                    <ChatIcon name="arrow-up" :size="16" color="#FFFFFF" />
                                </button>
                            </div>
                        </form>
                    </template>
                    <div v-else class="chat-empty bento-card">
                        <p class="bento-muted">Выберите обращение или создайте новое</p>
                    </div>
                </section>
            </div>
    </AmModal>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import AmButton from '@/components/ui/AmButton.vue'
import AmModal from '@/components/ui/AmModal.vue'
import AmSelect from '@/components/ui/AmSelect.vue'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'
import ChatSkeleton from '@/components/chat/ChatSkeleton.vue'
import {
    createSupportTicket,
    fetchSupportTicket,
    fetchSupportTickets,
    formatTicketCategory,
    formatTicketStatus,
    sendSupportTicketMessage,
    ticketStatusPillClass,
} from '@/utils/supportApi'

const categoryOptions = [
    { value: 'billing', label: 'Оплата' },
    { value: 'technical', label: 'Техническая' },
    { value: 'other', label: 'Другое' },
]

const props = defineProps({
    open: { type: Boolean, default: false },
    initialTicketId: { type: [Number, String], default: null },
    initialCategory: { type: String, default: '' },
})

const emit = defineEmits(['close', 'read'])

const tickets = ref([])
const listLoading = ref(false)
const selectedId = ref(null)
const selectedTicket = ref(null)
const showComposer = ref(false)
const creating = ref(false)
const replyText = ref('')
const sending = ref(false)
const chatLoading = ref(false)
const messagesEl = ref(null)
const isMobile = ref(false)

const mobileChatOnly = computed(() => isMobile.value && Boolean(selectedTicket.value))

const newTicket = ref({
    subject: '',
    message: '',
    category: 'other',
})

function updateMobileFlag() {
    if (typeof window === 'undefined') return
    isMobile.value = window.matchMedia('(max-width: 800px)').matches
}

function closeChatView() {
    selectedId.value = null
    selectedTicket.value = null
}

function formatDate(iso) {
    if (!iso) return ''
    return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
}

function formatTime(iso) {
    if (!iso) return ''
    return new Date(iso).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

async function loadList({ silent = false } = {}) {
    if (!silent) {
        listLoading.value = true
    }
    try {
        const { items } = await fetchSupportTickets({ per_page: 50 })
        tickets.value = items
    } catch {
        if (!silent) {
            tickets.value = []
        }
    } finally {
        if (!silent) {
            listLoading.value = false
        }
    }
}

function patchTicketInList(ticket) {
    if (!ticket?.id) return
    const idx = tickets.value.findIndex(t => t.id === ticket.id)
    if (idx === -1) {
        tickets.value = [ticket, ...tickets.value]
        return
    }
    tickets.value[idx] = { ...tickets.value[idx], ...ticket }
}

async function openTicket(id) {
    selectedId.value = id
    showComposer.value = false
    chatLoading.value = true
    try {
        selectedTicket.value = await fetchSupportTicket(id)
        emit('read')
        patchTicketInList({ ...selectedTicket.value, unread: false })
        await nextTick()
        if (messagesEl.value) {
            messagesEl.value.scrollTop = messagesEl.value.scrollHeight
        }
    } catch {
        selectedTicket.value = null
    } finally {
        chatLoading.value = false
    }
}

async function createTicket() {
    if (!newTicket.value.subject.trim() || !newTicket.value.message.trim()) return
    creating.value = true
    try {
        const ticket = await createSupportTicket({
            subject: newTicket.value.subject.trim(),
            message: newTicket.value.message.trim(),
            category: newTicket.value.category,
        })
        newTicket.value = { subject: '', message: '', category: 'other' }
        showComposer.value = false
        if (ticket?.id) {
            patchTicketInList(ticket)
            await openTicket(ticket.id)
        }
    } finally {
        creating.value = false
    }
}

async function sendMessage() {
    const body = replyText.value.trim()
    if (!body || !selectedId.value) return
    sending.value = true
    try {
        const data = await sendSupportTicketMessage(selectedId.value, body)
        replyText.value = ''
        selectedTicket.value = data?.ticket || await fetchSupportTicket(selectedId.value)
        patchTicketInList(selectedTicket.value)
        await nextTick()
        if (messagesEl.value) {
            messagesEl.value.scrollTop = messagesEl.value.scrollHeight
        }
    } finally {
        sending.value = false
    }
}

let mobileMq = null

onMounted(() => {
    updateMobileFlag()
    if (typeof window !== 'undefined') {
        mobileMq = window.matchMedia('(max-width: 800px)')
        mobileMq.addEventListener('change', updateMobileFlag)
    }
})

onBeforeUnmount(() => {
    mobileMq?.removeEventListener('change', updateMobileFlag)
})

watch(
    () => props.open,
    async (isOpen) => {
        if (!isOpen) {
            selectedId.value = null
            selectedTicket.value = null
            return
        }
        if (props.initialCategory) {
            newTicket.value.category = props.initialCategory
            showComposer.value = true
        }
        await loadList()
        if (props.initialTicketId) {
            await openTicket(Number(props.initialTicketId))
        }
    },
)
</script>

<style scoped>
.tickets-layout {
    display: grid;
    grid-template-columns: 300px 1fr;
    flex: 1;
    min-height: 0;
}

.tickets-list-panel {
    background: var(--bento-bg, #FAFAF7);
    padding: 16px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
    border-right: 1px solid var(--bento-border, #E8E6E0);
}

.composer {
    margin: 0;
    padding: 14px;
}

.composer-fields {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.composer-field {
    margin-bottom: 0;
}

.composer-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid var(--bento-border, #E8E6E0);
}

.ticket-items {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.ticket-item {
    background: white;
    border-radius: 10px;
    padding: 12px;
    cursor: pointer;
    border: 1px solid var(--bento-border, #E8E6E0);
    border-left: 3px solid transparent;
    transition: background 0.12s ease, border-color 0.12s ease;
}

.ticket-item:hover {
    background: var(--bento-hover, #F1EFE8);
}

.ticket-item--active {
    background: var(--bento-hover, #F1EFE8);
    border-left-color: var(--bento-dark, #23282C);
}

.ticket-item--unread {
    border-left-color: var(--bento-red, #E30613);
}

.ticket-item-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
}

.ticket-subject {
    font-size: 13px;
    font-weight: 600;
    color: var(--bento-text, #23282C);
    line-height: 1.35;
}

.unread-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--bento-red, #E30613);
    flex-shrink: 0;
    margin-top: 4px;
}

.ticket-item-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 8px;
}

.ticket-date {
    flex-shrink: 0;
    white-space: nowrap;
}

.ticket-preview {
    margin: 6px 0 0;
    line-height: 1.45;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.tickets-chat-panel {
    display: flex;
    flex-direction: column;
    min-height: 0;
    flex: 1;
    padding: 16px 20px;
    padding-bottom: max(16px, env(safe-area-inset-bottom));
    background: white;
    gap: 12px;
    overflow: hidden;
}

.chat-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin: 0;
    padding: 14px 16px;
    flex-shrink: 0;
}

.chat-back {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    margin-top: 2px;
    border: 1px solid var(--bento-border, #E8E6E0);
    border-radius: 6px;
    background: white;
    color: var(--bento-text, #23282C);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.chat-head-text {
    min-width: 0;
    flex: 1;
}

.chat-title {
    margin: 0 0 4px;
    font-size: 15px;
    font-weight: 600;
    color: var(--bento-text, #23282C);
}

.messages {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 4px 2px;
    -webkit-overflow-scrolling: touch;
}

.msg {
    display: flex;
    flex-direction: column;
    max-width: 82%;
}

.msg--user {
    align-self: flex-end;
    align-items: flex-end;
}

.msg--staff {
    align-self: flex-start;
}

.msg-meta {
    margin-bottom: 4px;
}

.msg-bubble {
    padding: 10px 14px;
    border-radius: 12px;
    font-size: 13px;
    line-height: 1.5;
}

.msg--user .msg-bubble {
    background: var(--bento-hover, #F1EFE8);
    color: var(--bento-text, #23282C);
    border: 1px solid var(--bento-border, #E8E6E0);
}

.msg--staff .msg-bubble {
    background: var(--bento-dark, #23282C);
    color: #FAFAF7;
}

.reply-form {
    flex-shrink: 0;
    padding-top: 4px;
}

.reply-card {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    max-width: 760px;
    margin: 0 auto;
    width: 100%;
    background: white;
    border: 1px solid var(--bento-border, #E8E6E0);
    border-radius: 12px;
    padding: 4px 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}

.reply-card:focus-within {
    border-color: var(--bento-dark, #23282C);
}

.reply-card--disabled {
    opacity: 0.7;
}

.reply-input {
    flex: 1;
    border: 0;
    background: transparent;
    padding: 10px 8px;
    font-size: 13px;
    font-family: inherit;
    color: var(--bento-text, #23282C);
    outline: none;
}

.reply-input::placeholder {
    color: var(--bento-muted, #888780);
}

.send-btn {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 6px;
    background: #888780;
    color: white;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s ease;
}

.send-btn:not(:disabled) {
    background: var(--bento-dark, #23282C);
}

.send-btn:not(:disabled):hover {
    background: #000;
}

.send-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.chat-empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    background: var(--bento-bg, #FAFAF7);
}

.state {
    font-size: 12.5px;
    padding: 12px;
    text-align: center;
}

@media (max-width: 800px) {
    .tickets-layout {
        grid-template-columns: 1fr;
        grid-template-rows: 1fr;
        min-height: 0;
        height: 100%;
    }

    .tickets-list-panel {
        min-height: 0;
        flex: 1;
        border-right: 0;
        border-bottom: 0;
        overflow-y: auto;
    }

    .tickets-layout--chat .tickets-chat-panel {
        padding: 12px 14px;
        padding-bottom: max(12px, env(safe-area-inset-bottom));
    }

    .tickets-layout--chat .messages {
        min-height: 0;
    }
}
</style>
